import {IOnBeforeActivationContext, IOnBeforeDeactivationContext, IWizardStep} from "./wizard.step.interface";
import {IServiceFactory} from "../../services/service-factory.interface";
import {IRoute} from "../../services/navigation/models/route.interface";
import {computed, IReactionDisposer, makeObservable, reaction} from "mobx";
import {DialogResult} from "../../services/dialog/dialog-enums";
import {Wizard} from "./wizard";
import {ValidationResultEnum, validationResultToDialogResult} from "../../types/validation-result.enum";
import {IRouteActivationOptions} from "../../services/navigation/navigator.service.interface";
import {Debounce} from "../../utils/debounce";
import {TimeSpan} from "../../types/time-span";

export abstract class WizardBaseStep implements IWizardStep {
    constructor(protected readonly services: IServiceFactory, protected readonly wizard: Wizard) {
        this._routeChangeReactionDebounce = new Debounce(TimeSpan.fromMilliseconds(200));
        makeObservable(this, {
            index: computed,
            wasVisited: computed
        });
    }

    abstract next(): Promise<void>;
    abstract get route(): IRoute;
    abstract get title(): string;

    private _routeChangeReactionDebounce: any = null;

    onAddedToWizard(): void {
        this._reactions.push(reaction(() => this.services.navigator.currentRoute, (route) => {

            if(this.route.equals(route)) {
                //debounce for the case we get consecutive reactions on the route.
                this._routeChangeReactionDebounce.execute(async () => {
                    await this._setActivated(this.wizard.previousStep);
                });
            }
        }, {
            fireImmediately: true
        }));
    }

    get index(): number {
        return this.wizard.findStepIndex(this);
    }

    protected _activateStepByRoute(route: IRoute, activateOptions?: IRouteActivationOptions): void {
        this.wizard.activateStepByRoute(route, activateOptions);
    }

    get wasVisited(): boolean {
        return this.wizard.getStepVisitedStatus(this.index);
    }

    private _reactions: IReactionDisposer[] = [];

    dispose(): void {
        this._routeChangeReactionDebounce.dispose();
        this._reactions.forEach(r => r());
        this._reactions = [];

    }

    get isActive(): boolean {
        return this.route.equals(this.services.navigator.currentRoute);
    }

    get isVisible(): boolean {
        return this._shouldBeDisplayed();
    }

    get allowSwipeForward(): boolean {
        return true;
    }

    protected _shouldBeDisplayed(): boolean {
        return true;
    }

    acceptActivation(): boolean {
        return true;
    }

    private async _setActivated(previousStep: IWizardStep | null) {
        this.wizard.setStepWasVisited(this.index);
        await this._onActivated(previousStep);
    }

    protected async _onActivated(previousStep: IWizardStep | null): Promise<void> {

    }

    async onBeforeActivation(context:IOnBeforeActivationContext): Promise<DialogResult> {

        return await this._onBeforeActivation(context);
    }

    protected async _onBeforeActivation(context:IOnBeforeActivationContext): Promise<DialogResult> {
        return DialogResult.Accepted;
    }


    async onBeforeDeactivation(context: IOnBeforeDeactivationContext): Promise<DialogResult> {
        if(context.routingGuardContext.isForwardNavigation && !context.routingGuardContext.targetLocationIsHomePage) {
            const result = await this._onBeforeForwardDeactivation(context);
            if(result !== DialogResult.Accepted) {
                return result;
            }
        }
        return await this._onBeforeDeactivation(context);
    }

    protected async _onBeforeDeactivation(context: IOnBeforeDeactivationContext): Promise<DialogResult> {
        return DialogResult.Accepted;
    }

    protected async _onBeforeForwardDeactivation(context: IOnBeforeDeactivationContext): Promise<DialogResult> {
        return validationResultToDialogResult(await this.validate())
    }

    async validate(): Promise<ValidationResultEnum> {
        return ValidationResultEnum.Success;
    }

    get allowNavigateToPreviousSteps(): boolean {
        return true;
    }

}
